import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import {
  Phone, MessageCircle, ShieldCheck, MapPin, Sparkles, Lock, Star, Clock,
  Anchor, Plane, ChefHat, Gem, HeartHandshake, ArrowRight, Quote,
} from "lucide-react";
import { api } from "@/lib/api";
import { trackLead, trackEvent } from "@/lib/analytics";
import { normalizeIndianPhone, openWhatsApp } from "@/lib/lead-helpers";
import {
  LUXURY_DESTINATIONS,
  LUXURY_COMPARISON,
  LUXURY_HONEYMOON_PHONE,
  LUXURY_HONEYMOON_PHONE_DISPLAY,
} from "@/config/honeymoon-experiences";

const WHATSAPP_LINE = LUXURY_HONEYMOON_PHONE.replace(/\D/g, "");

const TESTIMONIALS = [
  { name: "Ananya & Vikram",     from: "Mumbai",    quote: "The Riva speedboat in Positano was worth every rupee. Our concierge sent daily texts — I forgot I was talking to a travel company.", trip: "Amalfi Coast, 8 nights" },
  { name: "Karthik & Meera",     from: "Bengaluru", quote: "A helicopter landed on a glacier and served us breakfast. I've stopped trying to describe it to friends — I just show them the photos.", trip: "Switzerland, 9 nights" },
  { name: "Rohan & Ishani",      from: "Delhi NCR", quote: "The sandbank dinner in Maldives was the moment I proposed the second time. She said yes twice.", trip: "Maldives, 6 nights" },
  { name: "Aditya & Sanjana",    from: "Hyderabad", quote: "Doors-off helicopter over Nā Pali coast was other-worldly. Vacations Pro pre-planned every camera, every drone.", trip: "Hawaii, 10 nights" },
];

export default function LuxuryHoneymoon() {
  const navigate = useNavigate();
  const formRef = useRef(null);
  const conversionSent = useRef(false);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [destSlug, setDestSlug] = useState("");
  const [dates, setDates] = useState("");
  const [vision, setVision] = useState("");
  const [hp, setHp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tIdx, setTIdx] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    trackEvent("luxury_honeymoon_view", { campaign: "luxury_honeymoon_hnwi" });
  }, []);

  useEffect(() => {
    const id = setInterval(() => setTIdx((i) => (i + 1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(id);
  }, []);

  const scrollToForm = (slug) => {
    if (slug) setDestSlug(slug);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 40);
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (hp) return;
    const cleanPhone = normalizeIndianPhone(phone);
    if (!name.trim() || !cleanPhone) { toast.error("Please share your name and phone / WhatsApp number."); return; }
    if (cleanPhone.length !== 10) { toast.error("Enter a valid 10-digit Indian mobile number."); return; }
    setSubmitting(true);
    try {
      const utm = new URLSearchParams(window.location.search);
      const destName = LUXURY_DESTINATIONS.find((d) => d.slug === destSlug)?.name || "Multiple / Not sure";
      const payload = {
        name: name.trim(),
        email: email.trim() || null,
        phone: cleanPhone,
        destination: `Luxury Honeymoon · ${destName}`,
        preferences: [
          `Dates: ${dates || "flexible"}`,
          `Vision: ${vision || "not shared"}`,
        ].join(" · "),
        source: "lp_luxury_honeymoon",
        utm_source: utm.get("utm_source") || null,
        utm_medium: utm.get("utm_medium") || null,
        utm_campaign: utm.get("utm_campaign") || "luxury_honeymoon",
        utm_term: utm.get("utm_term") || null,
        utm_content: utm.get("utm_content") || null,
      };
      const { data: lead } = await api.post("/leads", payload);
      if (!conversionSent.current) {
        conversionSent.current = true;
        trackLead({
          source: "lp_luxury_honeymoon_form",
          destination: destName,
          value: 500000,
          label: "luxury_honeymoon_lead",
          extra: { lead_id: lead?.id || null, slug: destSlug },
        });
        try {
          if (typeof window.fbq === "function") {
            window.fbq("trackCustom", "LuxuryHoneymoonLead", { destination: destName, value: 500000, currency: "INR" });
          }
        } catch (err) { console.warn("fbq custom failed", err); }
        try {
          if (typeof window.gtag === "function") {
            window.gtag("event", "luxury_honeymoon_lead", { destination: destName, value: 500000, currency: "INR" });
          }
        } catch (err) { console.warn("gtag custom failed", err); }
      }
      const lines = [
        "Hi Vacations Pro — I would like to speak with a VIP concierge about a luxury honeymoon.",
        "",
        `Name: ${payload.name}`,
        `Phone / WhatsApp: +91 ${cleanPhone}`,
        `Email: ${payload.email || "—"}`,
        `Preferred destination: ${destName}`,
        `Travel dates: ${dates || "flexible"}`,
        vision ? `Vision: ${vision}` : "",
      ].filter(Boolean);
      // Mobile-friendly: wa.me + same-tab nav on phones; desktop opens new tab
      openWhatsApp(WHATSAPP_LINE, lines.join("\n"));
      toast.success("Received — WhatsApp is opening. A VIP concierge will call within 30 minutes.");
      setName(""); setPhone(""); setEmail(""); setDates(""); setVision("");
    } catch (err) {
      console.warn("Luxury LP submit failed", err);
      toast.error(err?.response?.data?.detail || "Couldn't submit — please call our concierge directly.");
    } finally { setSubmitting(false); }
  };

  const orgSchema = {
    "@context": "https://schema.org", "@type": "TravelAgency",
    "name": "Vacations Pro — VIP Concierge",
    "description": "Bespoke luxury honeymoon curators specialising in Amalfi, Switzerland, Hawaii, Bali, Maldives and Seychelles for Indian HNW couples.",
    "url": "https://vacationspro.com/honeymoon-experiences",
    "telephone": LUXURY_HONEYMOON_PHONE,
    "priceRange": "₹₹₹₹",
    "areaServed": ["IN", "IT", "CH", "US", "ID", "MV", "SC"],
  };
  const faqSchema = {
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "What's the price range for a Vacations Pro luxury honeymoon?",
        "acceptedAnswer": { "@type": "Answer", "text": "Our bespoke honeymoons begin at ₹4.2 L / couple (Bali, 7 nights) and scale to ₹18–25 L / couple for private-yacht itineraries in the Seychelles or a whole-island Maldives villa. Every itinerary is quoted 100% bespoke — never off a shelf." } },
      { "@type": "Question", "name": "Can you arrange business-class flights and private airport transfers?",
        "acceptedAnswer": { "@type": "Answer", "text": "Yes. Business-class sourcing (Emirates, Qatar, Etihad, ANA, Swiss), lounge access, priority visa services, and Rolls-Royce / Range Rover airport transfers are all part of the VIP service." } },
      { "@type": "Question", "name": "How quickly can you send an itinerary?",
        "acceptedAnswer": { "@type": "Answer", "text": "A senior concierge calls within 30 minutes of your enquiry. A hand-crafted preliminary itinerary is delivered within 24 hours." } },
      { "@type": "Question", "name": "Do you handle destination weddings and vow renewals?",
        "acceptedAnswer": { "@type": "Answer", "text": "Absolutely. Villa Cimbrone (Amalfi), Bulgari Bali cliff-deck, private Maldives sandbanks, glacier-top ceremonies in Switzerland — with photographer, celebrant and florist all curated by our team." } },
    ],
  };

  return (
    <div className="min-h-screen bg-[#f9f6f1] text-[#141210]" data-testid="luxury-honeymoon-page">
      <Helmet>
        <title>Luxury Honeymoon Experiences — Amalfi · Switzerland · Maldives | Vacations Pro VIP</title>
        <meta name="description" content="Bespoke luxury honeymoons for Indian HNW couples — private Riva charters in Amalfi, glacier helicopters in Switzerland, sandbank dinners in the Maldives. 24/7 VIP concierge on WhatsApp. Call +91 78883 70003." />
        <meta name="keywords" content="luxury honeymoon India, Amalfi honeymoon package, Switzerland honeymoon experiences, Maldives overwater villa, Bali cliffside villa, Seychelles honeymoon, Hawaii honeymoon India, HNW travel curator, private honeymoon concierge" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href="https://vacationspro.com/honeymoon-experiences" />

        <meta property="og:type" content="website" />
        <meta property="og:title" content="Arrive Somewhere Extraordinary — Luxury Honeymoons | Vacations Pro VIP" />
        <meta property="og:description" content="Private Riva speedboats · Glacier helicopters · Michelin dinners under the stars. Curated for Indian HNW couples. Call +91 78883 70003." />
        <meta property="og:url" content="https://vacationspro.com/honeymoon-experiences" />
        <meta property="og:image" content={LUXURY_DESTINATIONS[0].hero} />
        <meta property="og:image:width" content="1920" />
        <meta property="og:image:height" content="1080" />
        <meta property="og:site_name" content="Vacations Pro" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Arrive Somewhere Extraordinary — Luxury Honeymoons" />
        <meta name="twitter:description" content="Private Riva speedboats · Glacier helicopters · Michelin dinners under the stars. Curated for Indian HNW couples." />
        <meta name="twitter:image" content={LUXURY_DESTINATIONS[0].hero} />

        <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* STICKY HEADER */}
      <header className="sticky top-0 z-40 bg-[#f9f6f1]/95 backdrop-blur border-b border-[#e6dcc9] px-4 md:px-10 py-2.5 md:py-3 flex items-center justify-between gap-3">
        <button onClick={() => navigate("/")} className="flex-shrink-0" data-testid="lux-logo">
          <img src="/logo-header.png?v=blue3" alt="Vacations Pro" className="h-9 md:h-14 w-auto object-contain" />
        </button>
        <a
          href={`tel:${LUXURY_HONEYMOON_PHONE}`}
          onClick={() => trackLead({ source: "lp_lux_call_header", destination: "Luxury Honeymoon", label: "luxury_honeymoon_lead" })}
          className="inline-flex items-center gap-1.5 md:gap-2 bg-[#a8874b] hover:bg-[#96763d] text-white rounded-full px-3.5 md:px-5 py-2 md:py-2.5 text-[11px] md:text-xs tracking-[0.14em] uppercase font-semibold shadow-sm transition-colors whitespace-nowrap"
          data-testid="lux-header-call"
          aria-label={`Call VIP Concierge ${LUXURY_HONEYMOON_PHONE_DISPLAY}`}
        >
          <Phone className="w-3.5 h-3.5 shrink-0" />
          <span className="hidden md:inline">Call VIP · {LUXURY_HONEYMOON_PHONE_DISPLAY}</span>
          <span className="md:hidden">Call VIP</span>
        </a>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/1287460/pexels-photo-1287460.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1920"
            alt="Overwater villa at sunset — Vacations Pro luxury honeymoon"
            className="w-full h-full object-cover animate-[luxKen_18s_ease-in-out_infinite_alternate]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/45 to-black/85" />
        </div>
        <style>{`@keyframes luxKen { 0% { transform: scale(1.02) translateY(0); } 100% { transform: scale(1.08) translateY(-1%); } }`}</style>

        <div className="relative z-10 max-w-[1200px] mx-auto grid lg:grid-cols-[1.05fr_0.95fr] gap-12 px-6 md:px-12 pt-14 md:pt-24 pb-16 md:pb-28 text-white">
          <div>
            <div className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur border border-white/25 rounded-full px-3 py-1 text-[11px] tracking-[0.25em] uppercase font-semibold">
              <Gem className="w-3.5 h-3.5 text-[#e4c07c]" /> VIP Concierge · By Invitation
            </div>
            <h1 className="font-serif-vp text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight mt-6 drop-shadow-[0_4px_28px_rgba(0,0,0,0.55)]">
              Arrive Somewhere
              <span className="block text-[#e4c07c] italic">Extraordinary.</span>
            </h1>
            <p className="mt-6 text-base md:text-xl text-white/85 leading-relaxed max-w-[600px] font-serif-vp">
              A honeymoon is not a package. It is a private Riva speedboat waiting at your resort dock, a glacier
              landed on before breakfast, a dinner for two on a Maldives sandbank the world will never see.
              We do not book trips — we curate journeys, tailored entirely to your pace.
            </p>
            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <a
                href={`tel:${LUXURY_HONEYMOON_PHONE}`}
                onClick={() => trackLead({ source: "lp_lux_hero_call", destination: "Luxury Honeymoon", label: "luxury_honeymoon_lead" })}
                className="inline-flex items-center gap-2 bg-[#a8874b] hover:bg-[#96763d] text-white rounded-full px-7 py-4 text-[13px] tracking-[0.16em] uppercase font-bold transition-all hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(168,135,75,0.35)]"
                data-testid="lux-hero-call"
              >
                <Phone className="w-4 h-4" /> Call {LUXURY_HONEYMOON_PHONE_DISPLAY}
              </a>
              <button
                onClick={() => scrollToForm()}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/30 hover:bg-white/20 text-white rounded-full px-7 py-4 text-[13px] tracking-[0.16em] uppercase font-bold transition-colors"
                data-testid="lux-hero-cta"
              >
                Curate My Journey <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-3 max-w-[520px]">
              {[["20+", "Years"], ["1,800+", "Honeymoons"], ["24/7", "Concierge"]].map(([b, s]) => (
                <div key={s} className="bg-white/10 backdrop-blur border border-white/15 rounded-lg px-3 py-3 text-center">
                  <div className="font-serif-vp text-2xl text-[#e4c07c]">{b}</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-white/65 mt-0.5">{s}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Vertical whisper strip */}
          <aside className="hidden lg:flex flex-col justify-end pl-8 border-l border-white/15">
            <div className="text-[11px] tracking-[0.3em] uppercase text-white/60 mb-4">Signature Experiences</div>
            <ul className="space-y-4">
              {[
                { i: Anchor,   t: "Private Riva speedboats in Amalfi" },
                { i: Plane,    t: "Glacier helicopter breakfasts in Zermatt" },
                { i: ChefHat,  t: "Sandbank dinners in the Maldives" },
                { i: HeartHandshake, t: "Balinese vow-renewal at Tirta Empul" },
              ].map(({ i: Icon, t }) => (
                <li key={t} className="flex items-start gap-3 text-white/90 text-[15px] font-serif-vp">
                  <Icon className="w-4 h-4 text-[#e4c07c] mt-1" strokeWidth={1.4} />
                  {t}
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </section>

      {/* DESTINATION EXPERIENCE GRID */}
      <section className="px-6 md:px-12 lg:px-20 py-20 max-w-[1280px] mx-auto" data-testid="lux-destinations">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <span className="text-[11px] tracking-[0.32em] uppercase text-[#a8874b] font-semibold">Signature Destinations</span>
            <h2 className="font-serif-vp text-4xl md:text-5xl leading-tight text-[#141210] mt-3 max-w-3xl">
              Six worlds. One private curator.
            </h2>
          </div>
          <p className="text-sm text-[#5f574a] max-w-md">
            Every itinerary is designed room-by-room, flight-by-flight, chef-by-chef. Explore the experiences we
            arrange most often — then let us build yours from scratch.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {LUXURY_DESTINATIONS.map((d) => (
            <Link
              key={d.slug}
              to={`/honeymoon-experiences/${d.slug}`}
              className="group relative rounded-2xl overflow-hidden bg-black shadow-[0_10px_30px_rgba(0,0,0,0.08)] hover:shadow-[0_20px_50px_rgba(0,0,0,0.18)] transition-all duration-500"
              data-testid={`lux-dest-${d.slug}`}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img src={d.thumb} alt={`${d.name} — ${d.tagline}`}
                     className="w-full h-full object-cover transition-transform duration-[1800ms] group-hover:scale-[1.08]" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#e4c07c]">{d.country}</div>
                <h3 className="font-serif-vp text-3xl leading-tight mt-1.5">{d.name}</h3>
                <p className="text-[13px] text-white/85 mt-2 leading-relaxed">{d.heroLine}</p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase font-bold text-[#e4c07c] group-hover:gap-2.5 transition-all">
                  Explore {d.name} Experiences <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="bg-white border-y border-[#e6dcc9] px-6 md:px-12 lg:px-20 py-20" data-testid="lux-compare">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-[11px] tracking-[0.32em] uppercase text-[#a8874b] font-semibold">Standard vs Bespoke</span>
            <h2 className="font-serif-vp text-4xl md:text-5xl leading-tight text-[#141210] mt-3">
              Why our couples never go back to packages.
            </h2>
            <p className="text-sm text-[#5f574a] mt-4">
              The difference is not the destination. It is who plans, who greets, who cooks, and who takes the call at 3am.
            </p>
          </div>
          <div className="mt-10 rounded-2xl overflow-hidden border border-[#e6dcc9] shadow-sm">
            <div className="grid grid-cols-[1fr_1fr] bg-[#141210] text-white text-sm">
              <div className="px-5 md:px-8 py-4 border-r border-white/15 font-serif-vp text-lg">Standard Package</div>
              <div className="px-5 md:px-8 py-4 font-serif-vp text-lg text-[#e4c07c]">Vacations Pro VIP</div>
            </div>
            {LUXURY_COMPARISON.map((row, i) => (
              <div key={i} className={`grid grid-cols-[1fr_1fr] text-sm ${i % 2 ? "bg-[#faf7f1]" : "bg-white"}`}>
                <div className="px-5 md:px-8 py-4 text-[#7a6f5f] border-r border-[#e6dcc9]">{row.standard}</div>
                <div className="px-5 md:px-8 py-4 text-[#141210] font-medium">{row.private}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-[#141210] text-white px-6 md:px-12 py-16" data-testid="lux-trust">
        <div className="max-w-[1100px] mx-auto grid md:grid-cols-4 gap-6">
          {[
            { icon: ShieldCheck, title: "Discretion Guaranteed", body: "Your itinerary, photos and finances stay between you and one named concierge — never mass-emailed." },
            { icon: Sparkles,    title: "Zero Cookie-Cutter",    body: "No two itineraries we ship are the same. Ever. If you see it on Instagram, we've already moved on." },
            { icon: Clock,       title: "24/7 On Your WhatsApp", body: "One person. From your first call to your last flight home. Available every hour you are away." },
            { icon: HeartHandshake, title: "1,800+ Honeymoons",  body: "Since 2004. The couples we plan for send their friends. The friends send theirs. That's the entire funnel." },
          ].map((t) => (
            <div key={t.title} className="border border-white/10 rounded-xl p-5 bg-white/[0.03]">
              <t.icon className="w-5 h-5 text-[#e4c07c] mb-3" strokeWidth={1.5} />
              <div className="font-serif-vp text-lg">{t.title}</div>
              <p className="text-xs text-white/65 leading-relaxed mt-2">{t.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#faf7f1] px-6 md:px-12 lg:px-20 py-20 border-y border-[#e6dcc9]" data-testid="lux-testimonials">
        <div className="max-w-[900px] mx-auto text-center">
          <span className="text-[11px] tracking-[0.32em] uppercase text-[#a8874b] font-semibold">Couples</span>
          <h2 className="font-serif-vp text-4xl md:text-5xl leading-tight text-[#141210] mt-3">
            Whispers from newlyweds we&apos;ve sent home.
          </h2>
          <div className="mt-10 relative bg-white border border-[#e6dcc9] rounded-2xl p-8 md:p-12 shadow-[0_10px_40px_rgba(20,18,16,0.08)]">
            <Quote className="absolute top-5 right-6 w-16 h-16 text-[#e4c07c]/25" strokeWidth={1.2} />
            <p className="font-serif-vp text-xl md:text-3xl leading-snug text-[#141210]">
              &ldquo;{TESTIMONIALS[tIdx].quote}&rdquo;
            </p>
            <div className="mt-6 text-sm">
              <span className="font-semibold text-[#141210]">{TESTIMONIALS[tIdx].name}</span>
              <span className="text-[#7a6f5f]"> · {TESTIMONIALS[tIdx].from}</span>
              <div className="text-[12px] tracking-[0.2em] uppercase text-[#a8874b] mt-1">{TESTIMONIALS[tIdx].trip}</div>
            </div>
          </div>
          <div className="mt-5 flex items-center justify-center gap-2">
            {TESTIMONIALS.map((t, i) => (
              <button key={t.name} onClick={() => setTIdx(i)} aria-label={`Testimonial ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all ${i === tIdx ? "w-8 bg-[#a8874b]" : "w-2 bg-[#d5c7ad] hover:bg-[#a8874b]/50"}`}
                      data-testid={`lux-t-dot-${i}`} />
            ))}
          </div>
          <div className="mt-6 inline-flex items-center gap-1.5 text-[13px] text-[#5f574a]">
            <Star className="w-4 h-4 text-[#a8874b]" fill="currentColor" /> 4.9/5 across 340+ private honeymoons
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section className="px-6 md:px-12 lg:px-20 py-20 max-w-[1100px] mx-auto grid md:grid-cols-[1fr_1.15fr] gap-10 items-start" data-testid="lux-form-section">
        <div>
          <span className="text-[11px] tracking-[0.32em] uppercase text-[#a8874b] font-semibold">Curate My Journey</span>
          <h2 className="font-serif-vp text-4xl md:text-5xl leading-tight text-[#141210] mt-3">
            Speak with a human travel curator.
          </h2>
          <p className="text-[15px] text-[#5f574a] mt-5 leading-relaxed">
            Share a few details — dates, destination in mind, the kind of memory you want to build.
            A senior concierge calls within <strong>30 minutes</strong> to discuss. No forms, no auto-emails.
            Just a considered, in-person conversation.
          </p>
          <div className="mt-7 space-y-3 text-sm text-[#141210]">
            {[
              "24-hour bespoke itinerary preview, hand-signed by your concierge",
              "Business-class flight sourcing (Emirates · Qatar · Etihad · Swiss)",
              "Priority visa & Schengen assistance",
              "One private WhatsApp line for the length of your trip",
              "24/7 in-country support in Italy, Switzerland, Bali, Maldives, Seychelles & Hawaii",
            ].map((line) => (
              <div key={line} className="flex items-start gap-2.5">
                <Sparkles className="w-4 h-4 text-[#a8874b] mt-0.5 shrink-0" strokeWidth={1.5} />
                <span>{line}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 rounded-xl border border-[#e6dcc9] bg-[#faf7f1] p-5">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#a8874b] font-semibold">Prefer to talk now?</div>
            <a
              href={`tel:${LUXURY_HONEYMOON_PHONE}`}
              onClick={() => trackLead({ source: "lp_lux_form_side_call", destination: "Luxury Honeymoon", label: "luxury_honeymoon_lead" })}
              className="mt-2 font-serif-vp text-2xl md:text-3xl text-[#141210] hover:text-[#a8874b] transition-colors inline-flex items-center gap-2"
              data-testid="lux-form-side-call"
            >
              <Phone className="w-5 h-5" /> {LUXURY_HONEYMOON_PHONE_DISPLAY}
            </a>
            <div className="text-[11px] text-[#7a6f5f] mt-1">Direct to our VIP concierge desk · Mon–Sun · 9am–11pm IST</div>
          </div>
        </div>

        <form ref={formRef} onSubmit={submit}
              className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(20,18,16,0.12)] border border-[#e6dcc9] p-6 md:p-8 space-y-3"
              data-testid="lux-form">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#a8874b] font-semibold">Request Custom Itinerary</div>
          <h3 className="font-serif-vp text-2xl md:text-3xl text-[#141210] leading-snug">
            Tell us how you want to feel on your first married morning.
          </h3>

          <input type="text" name="hp" tabIndex={-1} autoComplete="off" aria-hidden="true"
                 value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" />

          <LuxInput label="Full Name" value={name} onChange={setName} required testid="lux-name" />
          <LuxInput label="Phone / WhatsApp (required)" type="tel" required testid="lux-phone"
                    value={phone} onChange={(v) => setPhone(normalizeIndianPhone(v))} />
          <LuxInput label="Email Address" type="email" value={email} onChange={setEmail} testid="lux-email" />

          <label className="block border border-[#e0d3b9] rounded-lg px-3 py-2 bg-white focus-within:border-[#a8874b] transition-colors">
            <div className="text-[10px] tracking-wider uppercase text-[#a8874b] font-semibold">Preferred Destination</div>
            <select value={destSlug} onChange={(e) => setDestSlug(e.target.value)}
                    className="w-full bg-transparent outline-none text-[15px] mt-0.5"
                    data-testid="lux-dest">
              <option value="">Not sure yet — advise me</option>
              {LUXURY_DESTINATIONS.map((d) => <option key={d.slug} value={d.slug}>{d.name}</option>)}
              <option value="custom">Somewhere else / custom</option>
            </select>
          </label>

          <LuxInput label="Estimated Travel Dates" value={dates} onChange={setDates}
                    placeholder="e.g. 12–20 April 2026, or flexible" testid="lux-dates" />

          <label className="block border border-[#e0d3b9] rounded-lg px-3 py-2 bg-white focus-within:border-[#a8874b] transition-colors">
            <div className="text-[10px] tracking-wider uppercase text-[#a8874b] font-semibold">Tell us what kind of experience you are dreaming of…</div>
            <textarea value={vision} onChange={(e) => setVision(e.target.value)} rows={3}
                      placeholder="Overwater villa · glacier heli · quiet cliffs · private chef every night…"
                      className="w-full bg-transparent outline-none text-[15px] mt-0.5 resize-none placeholder:text-[#a8977a]"
                      data-testid="lux-vision" />
          </label>

          <button type="submit" disabled={submitting}
                  className="w-full bg-[#141210] hover:bg-[#0a0908] text-[#e4c07c] rounded-xl py-4 text-[13px] tracking-[0.18em] uppercase font-bold transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2 mt-2"
                  data-testid="lux-submit">
            {submitting ? "Sending…" : "Request Custom Itinerary"} <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-[11px] text-[#7a6f5f] text-center inline-flex items-center justify-center gap-1.5 w-full">
            <Lock className="w-3 h-3" /> Encrypted · Never shared · Preview itinerary in 24 hours
          </p>
          <p className="text-center text-[12px] text-[#7a6f5f] pt-2 border-t border-[#e6dcc9] mt-4">
            Prefer to talk now?{" "}
            <a href={`tel:${LUXURY_HONEYMOON_PHONE}`} className="text-[#a8874b] font-semibold hover:underline" data-testid="lux-form-bottom-call">
              Call {LUXURY_HONEYMOON_PHONE_DISPLAY}
            </a>
          </p>
        </form>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#141210] text-white px-6 md:px-12 lg:px-20 py-20 text-center">
        <div className="max-w-[720px] mx-auto">
          <Gem className="w-8 h-8 text-[#e4c07c] mx-auto mb-5" strokeWidth={1.4} />
          <h2 className="font-serif-vp text-4xl md:text-6xl leading-[1.05]">
            Your once-in-a-lifetime — deserves once-in-a-lifetime planning.
          </h2>
          <p className="text-white/70 mt-5 text-sm md:text-base">
            One phone call, one dedicated curator, one honeymoon your friends will never stop asking about.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href={`tel:${LUXURY_HONEYMOON_PHONE}`}
               onClick={() => trackLead({ source: "lp_lux_final_call", destination: "Luxury Honeymoon", label: "luxury_honeymoon_lead" })}
               className="inline-flex items-center gap-2 bg-[#a8874b] hover:bg-[#96763d] text-white rounded-full px-8 py-4 text-[13px] tracking-[0.16em] uppercase font-bold transition-all hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(228,192,124,0.25)]"
               data-testid="lux-final-call">
              <Phone className="w-4 h-4" /> Call {LUXURY_HONEYMOON_PHONE_DISPLAY}
            </a>
            <button onClick={() => scrollToForm()}
                    className="inline-flex items-center gap-2 border border-white/25 hover:bg-white/10 rounded-full px-8 py-4 text-[13px] tracking-[0.16em] uppercase font-bold transition-colors"
                    data-testid="lux-final-form">
              Curate My Journey
            </button>
          </div>
        </div>
      </section>

      {/* Floating call button (mobile-friendly) */}
      <a
        href={`tel:${LUXURY_HONEYMOON_PHONE}`}
        onClick={() => trackLead({ source: "lp_lux_float_call", destination: "Luxury Honeymoon", label: "luxury_honeymoon_lead" })}
        className="fixed bottom-6 right-5 z-40 w-14 h-14 flex items-center justify-center bg-[#a8874b] hover:bg-[#96763d] text-white rounded-full shadow-[0_14px_35px_rgba(168,135,75,0.45)] transition-all hover:-translate-y-0.5"
        aria-label={`Call VIP concierge ${LUXURY_HONEYMOON_PHONE_DISPLAY}`}
        data-testid="lux-floating-call"
      >
        <Phone className="w-6 h-6" />
      </a>

      <footer className="px-6 md:px-12 py-8 text-center text-[11px] text-[#7a6f5f] flex items-center justify-center gap-2">
        <MapPin className="w-3.5 h-3.5" />
        K-10 Tower, Feroze Gandhi Market, Ludhiana 141001 · © {new Date().getFullYear()} Vacations Pro VIP
      </footer>
    </div>
  );
}

function LuxInput({ label, value, onChange, type = "text", placeholder = "", required = false, testid }) {
  return (
    <label className="block border border-[#e0d3b9] rounded-lg px-3 py-2 bg-white focus-within:border-[#a8874b] transition-colors">
      <div className="text-[10px] tracking-wider uppercase text-[#a8874b] font-semibold">{label}</div>
      <input type={type} value={value} required={required} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)}
             className="w-full bg-transparent outline-none text-[15px] mt-0.5 placeholder:text-[#a8977a]"
             data-testid={testid} />
    </label>
  );
}
